import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Form, Input, Modal, Popconfirm, Select, Space, Switch } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import { CALC_KINDS, errorText } from './format';
import { municipalitySelectProps, useMunicipalityOptions } from './municipalities';

/** A landlord or other income recipient: who they are and where their money goes. */
export default function RecipientModal({ open, company, recipient, projects, onClose, onSaved }) {
  const { t } = useTranslation('taxReturns');
  const [form] = Form.useForm();
  const account = Form.useWatch('account', form);
  const municipalities = useMunicipalityOptions(open);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(
      recipient
        ? { ...recipient, project: recipient.project?._id || null }
        : { idType: 'jmbg', kinds: ['rent'], active: true }
    );
  }, [open, recipient, form]);

  const save = useMutation({
    mutationFn: (values) => {
      // A cleared Select leaves the field out; null clears it on the person.
      const v = { ...values, municipalityCode: values.municipalityCode ?? null, abPartnerCode: values.abPartnerCode || null };
      return recipient ? API.taxReturns.updateRecipient(company, recipient._id, v) : API.taxReturns.createRecipient(company, v);
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => API.taxReturns.deleteRecipient(company, recipient._id),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const err = save.error || remove.error;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={640}
      destroyOnClose
      title={recipient ? recipient.name : t('people.addRecipient')}
      footer={
        <Space>
          {recipient && (
            <Popconfirm title={t('people.deleteConfirm')} onConfirm={() => remove.mutate()}>
              <Button shape="round" danger icon={<DeleteOutlined />} loading={remove.isPending}>
                {t('people.delete')}
              </Button>
            </Popconfirm>
          )}
          <Button shape="round" onClick={onClose}>
            {t('people.cancel')}
          </Button>
          <Button shape="round" type="primary" loading={save.isPending} onClick={() => form.submit()}>
            {t('people.save')}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Form.Item name="name" label={t('people.fields.name')} rules={[{ required: true, message: t('errors.recipient_name_required') }]}>
          <Input />
        </Form.Item>
        <Space wrap align="start">
          <Form.Item name="idType" label={t('people.fields.idType')}>
            <Select
              style={{ width: 130 }}
              options={['jmbg', 'passport'].map((v) => ({ value: v, label: t(`people.idTypes.${v}`) }))}
            />
          </Form.Item>
          <Form.Item name="idNumber" label={t('people.fields.idNumber')}>
            <Input style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="project" label={t('fields.project')}>
            <Select
              allowClear
              style={{ width: 220 }}
              options={projects.map((p) => ({ value: p._id, label: `${p.code} ${p.name}` }))}
            />
          </Form.Item>
        </Space>
        <Space wrap align="start">
          <Form.Item name="address" label={t('people.fields.address')}>
            <Input style={{ width: 340 }} />
          </Form.Item>
          <Form.Item name="municipalityCode" label={t('people.fields.municipality')} tooltip={t('people.helpText.municipality')}>
            <Select
              {...municipalitySelectProps}
              allowClear
              style={{ width: 220 }}
              options={municipalities}
              placeholder={t('pppd.asCompany')}
            />
          </Form.Item>
        </Space>
        <Space wrap align="start">
          <Form.Item name="account" label={t('people.fields.account')}>
            <Input style={{ width: 240 }} placeholder="160-0000000000000-00" />
          </Form.Item>
          {account && (
            <Form.Item name="accountHolder" label={t('people.fields.accountHolder')} tooltip={t('people.helpText.accountHolder')}>
              <Input style={{ width: 260 }} />
            </Form.Item>
          )}
          <Form.Item name="abPartnerCode" label={t('people.fields.abPartnerCode')} tooltip={t('people.helpText.abPartnerCode')}>
            <Input style={{ width: 140 }} placeholder="M-LEC" />
          </Form.Item>
        </Space>
        <Space wrap align="start" size="large">
          <Form.Item name="kinds" label={t('people.fields.kinds')} rules={[{ required: true }]}>
            <Checkbox.Group options={CALC_KINDS.map((k) => ({ value: k, label: t(`calc.kinds.${k}`) }))} />
          </Form.Item>
          <Form.Item name="active" label={t('people.fields.active')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Space>
        <Form.Item name="note" label={t('fields.note')}>
          <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
        </Form.Item>
        {recipient?.externalCode && (
          <p className="text-xs opacity-60">
            {t('people.fields.externalCode')}: {recipient.externalCode}
          </p>
        )}
      </Form>
      {err && <Alert type="error" showIcon message={errorText(t, err)} />}
    </Modal>
  );
}
